import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import { EquipmentDTO, EquipmentTypeDTO, PmPlanDTO } from '../../interfaces/maintenance';
import { cell } from './woCommon';
import { PM_STATE_COLOR, PM_STATE_LABEL, pmDueHint, pmNextDueText, pmTriggerText } from './pmCommon';

/** PM plans of the institution, with filters. Managers can add plans and apply a plan to many machines. */
const PmPlanList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_PM_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<PmPlanDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [typeId, setTypeId] = useState('');
    const [equipmentId, setEquipmentId] = useState('');
    const [active, setActive] = useState<'' | 'true' | 'false'>('');
    const [types, setTypes] = useState<EquipmentTypeDTO[]>([]);
    const [equipment, setEquipment] = useState<EquipmentDTO[]>([]);

    useEffect(() => {
        MaintenanceService.listTypes(institutionId).then(setTypes).catch(() => setTypes([]));
        MaintenanceService.listEquipment(institutionId, {}, 0, 50).then(r => setEquipment(r.content)).catch(() => setEquipment([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const searchEquipment = (text: string) => {
        MaintenanceService.listEquipment(institutionId, { q: text }, 0, 50).then(r => setEquipment(r.content)).catch(() => undefined);
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await MaintenanceService.listPmPlans(
                institutionId, { equipmentId, typeId, active: active === '' ? '' : active === 'true' }, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load PM plans');
        } finally {
            setLoading(false);
        }
    }, [institutionId, equipmentId, typeId, active, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.pmPlans.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Preventive maintenance plans</Typography>
                {canManage && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button data-help="maint.pmPlans.many" variant="outlined" onClick={() => navigate('/maintenance/pm-plans/new?mode=many')}>Apply to many</Button>
                        <Button data-help="maint.pmPlans.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/maintenance/pm-plans/new')}>New plan</Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.pmPlans.filter">
                <Box sx={cell(220)}>
                    <SearchableSelect
                        options={equipment} value={equipmentId} size="small" label="Machine" data-help="maint.pmPlans.filter.machine"
                        getOptionLabel={e => `${e.code} - ${e.name}`}
                        disableClientFilter onInputChange={searchEquipment}
                        onChange={id => { setEquipmentId(id); setPage(0); }}
                    />
                </Box>
                <Box sx={cell(200)}>
                    <SearchableSelect
                        options={types} value={typeId} size="small" label="Machine type" data-help="maint.pmPlans.filter.type"
                        getOptionLabel={t => t.name} onChange={id => { setTypeId(id); setPage(0); }}
                    />
                </Box>
                <TextField
                    select size="small" label="Active" value={active} sx={cell(130)} data-help="maint.pmPlans.filter.active"
                    onChange={e => { setActive(e.target.value as '' | 'true' | 'false'); setPage(0); }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                </TextField>
            </Box>

            <Paper data-help-section="maint.pmPlans.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.pmPlans.col.plan">Plan</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.machine">Machine</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.repeats">Repeats</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.next">Next due</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.last">Last done</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.order">Open order</TableCell>
                                    <TableCell data-help="maint.pmPlans.col.state">State</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No PM plans found</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/maintenance/pm-plans/${r.id}`)}>
                                        <TableCell>{r.name}</TableCell>
                                        <TableCell>{r.equipmentCode} {r.equipmentName}</TableCell>
                                        <TableCell>{pmTriggerText(r)}</TableCell>
                                        <TableCell>{pmNextDueText(r)}</TableCell>
                                        <TableCell>{r.lastDoneDate ?? '-'}</TableCell>
                                        <TableCell>{r.openWorkOrderNo ?? '-'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={PM_STATE_LABEL[r.state]} color={PM_STATE_COLOR[r.state]} />
                                            {pmDueHint(r) && <Typography variant="caption" sx={{ ml: 1 }}>{pmDueHint(r)}</Typography>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[10, 20, 50]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
        </Box>
    );
};

export default PmPlanList;
